import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { Card } from "@/components/PortalPage";
import { money } from "@/lib/billing";
import { toast } from "sonner";

const Item = ({ r, onDone }) => {
  const [busy, setBusy] = useState(false);
  const kind = r.group.slice(0, -1);
  const go = async (choice) => {
    setBusy(true);
    try { await api(`/billing/import-review/${r.plan_id}/${r.row_id}`, { method: "POST", body: { choice } }); toast.success(choice === "keep" ? "Kept as is" : "Fixed"); onDone(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const f = r.financial || {};
  return (
    <div className="border-t border-[#2a2a2a] py-3" data-testid={`import-review-item-${r.row_id}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <b className="capitalize">{kind}{r.alr_number ? ` #${r.alr_number}` : r.group === "customers" ? "" : ` (Jobber ${r.source_number})`}</b>
        {r.group !== "jobs" && f.source_total !== undefined && <span className="text-xs text-[#aaa]">Jobber total {money(f.source_total)}{f.source_balance !== null && f.source_balance !== undefined ? ` · Jobber balance ${money(f.source_balance)}` : ""}</span>}
        {r.record_id && r.group !== "customers" && <Link to={`/billing/${r.group === "jobs" ? "job" : "doc"}/${r.record_id}`} className="text-xs font-bold text-[var(--accent)]" data-testid={`import-review-open-${r.row_id}`}>Open</Link>}
      </div>
      {(r.issues || []).map((t, i) => <p key={i} className="mt-1 text-xs text-[#FFB800]">{t}</p>)}
      {!(r.issues || []).length && (r.evidence || []).slice(0, 2).map((t, i) => <p key={i} className="mt-1 text-xs text-[#aaa]">{t}</p>)}
      {r.decision && <p className="mt-1 text-xs font-bold">{r.decision}</p>}
      {r.franchisee_note && <p className="mt-1 text-xs text-[#ccc]">From ALR: {r.franchisee_note}</p>}
      {r.decided ? <p className="mt-2 text-xs text-[var(--accent)]" data-testid={`import-review-done-${r.row_id}`}>Done: {r.decided.choice === "keep" ? "kept as is" : "fix applied"} · {r.decided.by}</p> : (
        <div className="mt-2 flex flex-wrap gap-2">
          {r.can_accept && <button disabled={busy} onClick={() => go("accept")} data-testid={`import-review-accept-${r.row_id}`} className="rounded-lg bg-[var(--accent)] px-3 py-2 text-xs font-extrabold text-black disabled:opacity-40">{r.proposal_label}</button>}
          <button disabled={busy} onClick={() => go("keep")} data-testid={`import-review-keep-${r.row_id}`} className="rounded-lg border border-[#444] px-3 py-2 text-xs font-bold disabled:opacity-40">Keep as is</button>
        </div>
      )}
    </div>
  );
};

export const ImportReviewCard = () => {
  const [d, setD] = useState(null);
  const load = useCallback(() => api("/billing/import-review").then(setD).catch(() => setD(null)), []);
  useEffect(() => { load(); }, [load]);
  if (!d || !d.rows.length) return null;
  return (
    <Card className="border-[#FFB800]/60" data-testid="import-review-card">
      <p className="flex items-center gap-2 text-xs font-bold tracking-[2px] text-[#FFB800]"><AlertTriangle size={14} />JOBBER IMPORT: {d.open ? `${d.open} ITEM${d.open === 1 ? "" : "S"} TO CHECK` : "ALL CHECKED"}</p>
      <p className="mt-1 text-xs text-[#ccc]">These records came over from Jobber but don't line up exactly. Check each one, then use the fix or keep it as it is.</p>
      {d.rows.map((r) => <Item key={r.row_id} r={r} onDone={load} />)}
    </Card>
  );
};
